import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useSearchParams, useNavigate } from "react-router-dom"; // ✅ added useNavigate
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';

import Overview from './Overview';
import PlanSelectionPage from './PlanSelectionPage';
import PaymentHistory from './PaymentHistory';
import PaymentMethod from './PaymentMethod';
import SubscriptionLog from "./SubscriptionLog";

// --- Define tab slugs (keep consistent) ---
const tabSlugs = [
    'overview',
    'cards',
    'plan',
    'subscriptions',
    'payments'
];

// --- TabPanel component ---
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: '100%' }}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}
TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};
function a11yProps(index) {
    return {
        id: `vertical-tab-${index}`,
        "aria-controls": `vertical-tabpanel-${index}`,
    };
}

export default function Billing() {
    const navigate = useNavigate(); // ✅ navigation hook

    useEffect(() => {
        document.title = 'Billing | KlonIT Web Application';

        // ✅ Role check
        const userStr = localStorage.getItem("user");
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                const role = user?.active_team?.user_role_in_team || "";
                if (role.toLowerCase() !== "admin") {
                    console.warn("Billing: Non-admin user detected, redirecting to /chatbots");
                    navigate("/chatbots", { replace: true });
                }
            } catch (err) {
                console.error("Billing: Failed to parse user from localStorage", err);
                navigate("/chatbots", { replace: true }); // fallback redirect
            }
        } else {
            console.warn("Billing: No user found, redirecting to /chatbots");
            navigate("/chatbots", { replace: true });
        }
    }, [navigate]);

    const [searchParams, setSearchParams] = useSearchParams();
    const getTabIndexFromUrl = useCallback(() => {
        const tabQuery = searchParams.get('tab')?.toLowerCase();
        if (tabQuery) {
            const index = tabSlugs.indexOf(tabQuery);
            if (index !== -1) return index;
        }
        return 0;
    }, [searchParams]);

    const [value, setValue] = useState(getTabIndexFromUrl);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    useEffect(() => {
        const indexFromUrl = getTabIndexFromUrl();
        if (indexFromUrl !== value) {
            setValue(indexFromUrl);
        }
        const currentTabSlugInUrl = searchParams.get('tab')?.toLowerCase();
        if (!currentTabSlugInUrl || tabSlugs.indexOf(currentTabSlugInUrl) === -1) {
            setSearchParams({ tab: tabSlugs[value] }, { replace: true });
        }
    }, [searchParams, value, getTabIndexFromUrl, setSearchParams]);

    const handleChange = (event, newValue) => {
        if (newValue !== value) {
            setValue(newValue);
            const newSlug = tabSlugs[newValue];
            setSearchParams({ tab: newSlug });
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };
    const showMessage = (message, severity = "success") => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handlePlanChangeComplete = useCallback((successMessage) => {
        showMessage(successMessage || "Subscription updated successfully!", "success");
        const overviewIndex = tabSlugs.indexOf('overview');
        if (overviewIndex !== -1) {
            setValue(overviewIndex);
            setSearchParams({ tab: 'overview' });
        }
    }, [setSearchParams]);

    const handlePlanChangeError = useCallback((errorMessage) => {
        showMessage(errorMessage || "An error occurred while updating the subscription.", "error");
    }, []);

    return (
        <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
              <Typography variant="h5" component="h1" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">
               Billing Management
              </Typography>
              <Divider sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', flexGrow: 1, mt: { xs: 0, md: 3 } }} >
                    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                            <Box sx={{
                                borderColor: 'divider',
                                mb: { xs: 2, md: 0 },
                                mr: { xs: 0, md: 2 },
                                minWidth: '180px'
                            }}>
                                <Tabs
                                    orientation="vertical"
                                    variant="scrollable"
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="Vertical billing tabs"
                                    sx={{
                                        borderRight: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    <Tab label="Overview" {...a11yProps(0)} />
                                    <Tab label="Manage Cards" {...a11yProps(1)} />
                                    <Tab label="Manage Plan" {...a11yProps(2)} />
                                    <Tab label="Subscription History" {...a11yProps(3)} />
                                    <Tab label="Payment History" {...a11yProps(4)} />
                                </Tabs>
                            </Box>

                            <Box sx={{ flexGrow: 1, width: '100%' }}>
                                <TabPanel value={value} index={0}>
                                    <Overview key={`overview-tab`} />
                                </TabPanel>
                                <TabPanel value={value} index={1}>
                                    <PaymentMethod />
                                </TabPanel>
                                <TabPanel value={value} index={2}>
                                    <PlanSelectionPage
                                        onComplete={handlePlanChangeComplete}
                                        onError={handlePlanChangeError}
                                    />
                                </TabPanel>
                                <TabPanel value={value} index={3}>
                                    <SubscriptionLog />
                                </TabPanel>
                                <TabPanel value={value} index={4}>
                                    <PaymentHistory />
                                </TabPanel>
                            </Box>
                        </Box>
                    </Box>

                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={6000}
                        onClose={handleSnackbarClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Box>
        </Paper>
    );
}
